import { createElement, useMemo, useState } from 'react';
import { Banner, Button, Checkbox, FieldLabel, Modal, TextField } from '@scripsteam/scrips-signal-ds';
import { Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { showServerError, showSuccess } from '@/shared/lib/toast-helpers';
import { useCreateOrderSet } from '@/settings/services/use-order-sets';
import { orderSetTypeLucide } from '@/settings/components/order-sets/order-set-item-details';
import type { CodedOrderPayload } from '@/encounter/lib/order-payload';
import type { OrderSetItemType } from '@/types/order-set-api';
import {
  buildSaveAsGroups,
  groupState,
  initialTicks,
  saveAsError,
  savedToast,
  toggleGroup,
  toggleRow,
  toSaveAsBody,
  type SaveAsDx,
  type SaveAsDxCoding,
  type SaveAsGroup,
  type SaveAsOrder,
  type SaveAsRow,
} from './save-as-order-set';

/**
 * Save as order set (PROD-2722, v3 board 7 "CREATE a set from this visit (Save as order set, A/P
 * ⋯ menu): not the apply flow"). Name the set, tick the orders to include, Save. One Signal DS
 * Modal (lg, 640px), drawn like the Settings add / edit modal (`order-set-edit-dialog.tsx`): the
 * DS Modal has no Close button and titles at 18px, so the board's 17px title and 28px Close are
 * drawn here and the DS title stays as the dialog's accessible name, visually hidden.
 *
 * After the POST (board 8, and Andrew's ruling): the app's normal success toast points to
 * Settings › Order sets. No draft, no new tab, and this visit is not touched.
 *
 * The caller MOUNTS this per open, so every open starts from the visit as it is now.
 */
export interface SaveAsOrderSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diagnoses: readonly SaveAsDx[];
  orders: readonly SaveAsOrder[];
  payloads: ReadonlyMap<string, CodedOrderPayload>;
  dxCoding?: SaveAsDxCoding;
}

const TITLE = 'Save as order set';
const NAME_HELP = 'You can rename it or change the orders later in Settings › Order sets.';
const KEPT =
  'Kept as defaults: each order and its dose, frequency, quantity, lab and priority. Not kept: the patient, dates, notes to the lab, order and lot numbers. This visit stays as it is.';

export function SaveAsOrderSetDialog({
  open,
  onOpenChange,
  diagnoses,
  orders,
  payloads,
  dxCoding,
}: SaveAsOrderSetDialogProps) {
  // The visit as it was when the dialog opened: a later autosave must not reshuffle the list.
  const [groups] = useState(() => buildSaveAsGroups(diagnoses, orders, payloads, dxCoding));
  const [ticks, setTicks] = useState(() => initialTicks(groups));
  const [name, setName] = useState('');
  const create = useCreateOrderSet();

  const close = () => onOpenChange(false);
  const error = saveAsError(name, groups, ticks);
  const pending = create.isPending;
  const hasRows = useMemo(() => groups.some((g) => g.rows.length > 0), [groups]);

  const handleSave = () => {
    if (error || pending) return;
    const body = toSaveAsBody(name, groups, ticks);
    create.mutate(body, {
      onSuccess: () => {
        showSuccess(savedToast(body.name));
        close();
      },
      onError: (err) => showServerError('order-sets.create', err),
    });
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={TITLE}
      titleHidden
      size="lg"
      footerAlign="between"
      footer={
        // The board's footer: Save order set + Cancel on the left, over a rule.
        <div className="flex flex-1 items-center justify-between gap-2 pt-4 border-t border-border-subtle">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="compact"
              className="px-4"
              onClick={handleSave}
              disabled={!!error || pending}
            >
              {pending ? 'Saving…' : 'Save order set'}
            </Button>
            <Button type="button" variant="secondary" size="compact" className="px-4" onClick={close} disabled={pending}>
              Cancel
            </Button>
          </div>
        </div>
      }
    >
      {/* The board's header. Hidden from assistive tech: the DS title above names the dialog. */}
      <div className="flex items-start justify-between gap-3">
        <p aria-hidden="true" className="m-0 text-title font-semibold text-text-primary">
          {TITLE}
        </p>
        <button
          type="button"
          aria-label="Close"
          onClick={close}
          className="icon-action"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <div className="mt-4 flex flex-col gap-4 max-h-overlay-body overflow-y-auto scrollbar-hide">
        <div className="min-w-0">
          {/* The board's mark sits 2px after the label; the DS `required` marker sits 4px. */}
          <FieldLabel stacked className="mb-2" htmlFor="save-as-order-set-name">
            Name
            <span aria-hidden="true" className="ml-1 text-status-error">
              *
            </span>
          </FieldLabel>
          <TextField
            id="save-as-order-set-name"
            size="compact"
            aria-required
            autoFocus
            value={name}
            maxLength={100}
            disabled={pending}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="mt-1 mb-0 text-xs text-text-muted">{NAME_HELP}</p>
        </div>

        <div>
          <FieldLabel stacked className="mb-2">
            Orders to include
          </FieldLabel>
          {hasRows ? (
            <div className="rounded-md border border-border-subtle overflow-hidden" role="group" aria-label="Orders to include">
              {groups.map((group, gi) => (
                <GroupRows
                  key={group.key ?? `loose-${gi}`}
                  group={group}
                  ticks={ticks}
                  disabled={pending}
                  onChange={setTicks}
                />
              ))}
            </div>
          ) : (
            <p className="m-0 text-sm text-text-muted">This visit has no orders to save yet.</p>
          )}
        </div>

        <Banner variant="info" icon={<Info className="size-4" aria-hidden />}>
          <span className="text-text-secondary">{KEPT}</span>
        </Banner>
      </div>
    </Modal>
  );
}

const ROW = 'flex items-center gap-3 py-2 pr-3 border-b border-border-subtle last:border-b-0';

function GroupRows({
  group,
  ticks,
  disabled,
  onChange,
}: {
  group: SaveAsGroup;
  ticks: Set<string>;
  disabled: boolean;
  onChange: (next: Set<string>) => void;
}) {
  const state = groupState(group, ticks);
  return (
    <>
      {group.key && (
        <div className={cn(ROW, 'pl-3 bg-grey-100')} data-slot="save-as-diagnosis">
          <Checkbox
            aria-label={group.display}
            checked={state.checked}
            indeterminate={state.indeterminate}
            disabled={disabled}
            onCheckedChange={(on) => onChange(toggleGroup(group, ticks, on))}
          />
          {group.code && (
            <span className="inline-flex items-center h-5 px-2 rounded-md bg-grey-200 text-grey-800 text-size-eyebrow font-mono tracking-label whitespace-nowrap">
              {group.code}
            </span>
          )}
          <span className="flex-1 min-w-0 text-sm font-semibold text-text-primary">{group.display}</span>
          <span className="text-size-eyebrow text-text-muted">Diagnosis</span>
        </div>
      )}
      {group.rows.map((row) => (
        <OrderRow
          key={row.key}
          row={row}
          checked={ticks.has(row.key)}
          disabled={disabled}
          onToggle={(on) => onChange(toggleRow(group, row, ticks, on))}
        />
      ))}
    </>
  );
}

function OrderRow({
  row,
  checked,
  disabled,
  onToggle,
}: {
  row: SaveAsRow;
  checked: boolean;
  disabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <div className={cn(ROW, 'pl-10')} data-slot="save-as-order">
      <Checkbox aria-label={row.label} checked={checked} disabled={disabled} onCheckedChange={onToggle} />
      <span className="inline-flex text-text-secondary" aria-hidden="true">
        <TypeGlyph type={row.item.type} />
      </span>
      <span className="flex-1 min-w-0 text-sm text-text-primary">{row.label}</span>
      {row.meta && <span className="text-size-eyebrow text-text-hint tabular-nums">{row.meta}</span>}
    </div>
  );
}

/** An order row's type mark: the order-set type's Lucide glyph (icon pass, 16 Oct), 16, secondary ink. */
function TypeGlyph({ type }: { type: OrderSetItemType }) {
  return createElement(orderSetTypeLucide(type), { className: 'size-4' });
}
